// frontend/src/features/novs/NOVSPage.jsx
// NOVSPage: wiring + state for Notice of Value Scraper.
// Mirrors the TBS flow but targets /novs endpoints and CADs instead of tax collectors.

"use client";

import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../apiClient";
import JSZip from "jszip";
import { COLLECTORS } from "./collectors";
import { REGION_ORDER } from "../tbs/regions";
import { flushSync } from "react-dom";
import NOVSView from "./NOVSView";

const paint = () => new Promise(requestAnimationFrame);

export default function NOVSPage({ onClose, logout }) {
  const navigate = useNavigate();

  const COMING_SOON = useMemo(
    () => new Set(COLLECTORS.filter((c) => c.comingSoon).map((c) => c.key)),
    []
  );

  // CADs that require portal authentication
  const AUTH_REQUIRED_CADS = useMemo(
    () => new Set(COLLECTORS.filter((c) => c.requiresAuth).map((c) => c.key)),
    []
  );

  const LONG_CADS = useMemo(() => new Set(["denton", "collin", "hcad", "travis", "rockwall", "cameron", "hidalgo", "montgomery", "bexar", "tarrant"]), []);

  const CAD_TIMEOUT_MS = (key) => (LONG_CADS.has(String(key)) ? 180_000 : 25_000);

  const SLOW_COLLECTORS = useMemo(
    () =>
      new Set(COLLECTORS.filter((c) => /🟡/.test(c.name)).map((c) => c.key)),
    []
  );

  // Inputs
  const [collectorInput, setCollectorInput] = useState("");
  const [accountInput, setAccountInput] = useState("");
  const [yearInput, setYearInput] = useState(""); // required for Collin
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  // Queue & log
  const [queue, setQueue] = useState([]); // [{id, collector, account, year?, status, filename}]
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedCount, setSavedCount] = useState(0);

  const [showCollectors, setShowCollectors] = useState(false);
  const [collectorSearch, setCollectorSearch] = useState("");

  // ── Portal credential modal (for auth-required CADs like Tarrant) ──
  const [showCredModal, setShowCredModal] = useState(false);
  const [credUsername, setCredUsername] = useState("");
  const [credPassword, setCredPassword] = useState("");
  const [credRemember, setCredRemember] = useState(false);
  const credResolveRef = useRef(null);

  // Load remembered credentials on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem("novs_tarrant_creds");
      if (saved) {
        const { username, password } = JSON.parse(saved);
        if (username) setCredUsername(username);
        if (password) setCredPassword(password);
        setCredRemember(true);
      }
    } catch {}
  }, []);

  function waitForCredentials() {
    return new Promise((resolve) => {
      credResolveRef.current = resolve;
      setShowCredModal(true);
    });
  }

  const handleCredSubmit = useCallback(() => {
    if (credRemember) {
      localStorage.setItem(
        "novs_tarrant_creds",
        JSON.stringify({ username: credUsername, password: credPassword })
      );
    } else {
      localStorage.removeItem("novs_tarrant_creds");
    }
    setShowCredModal(false);
    if (credResolveRef.current) {
      credResolveRef.current({ username: credUsername, password: credPassword });
      credResolveRef.current = null;
    }
  }, [credRemember, credUsername, credPassword]);

  const handleCredCancel = useCallback(() => {
    setShowCredModal(false);
    if (credResolveRef.current) {
      credResolveRef.current(null);
      credResolveRef.current = null;
    }
  }, []);

  // Region sections
  const REGION_GROUPS = useMemo(() => {
    const map = new Map();
    const seen = new Set();

    // Seed known regions in order
    REGION_ORDER.forEach((r) => map.set(r, []));

    // Collect all CADs into region buckets (including regions not in REGION_ORDER)
    for (const c of COLLECTORS) {
      if (seen.has(c.key)) continue;
      seen.add(c.key);

      const region = c.region || "Other / Special";
      if (!map.has(region)) map.set(region, []);
      map.get(region).push(c);
    }

    // Build final ordered list: REGION_ORDER first, then any missing regions (alphabetical),
    // then "Other / Special" last if present.
    const extraRegions = Array.from(map.keys()).filter((r) => !REGION_ORDER.includes(r));
    extraRegions.sort((a, b) => a.localeCompare(b));

    const regions = [
      ...REGION_ORDER,
      ...extraRegions.filter((r) => r !== "Other / Special"),
      ...(map.has("Other / Special") ? ["Other / Special"] : []),
    ];

    return regions.map((r) => ({
      region: r,
      collectors: (map.get(r) || []).sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [collectorSearch]);

  // Search across CADs
  const SEARCH_RESULTS = useMemo(() => {
    const raw = collectorSearch.trim();
    if (!raw) return [];
    const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
    const q = norm(raw);

    const out = [];
    const seen = new Set();
    for (const c of COLLECTORS) {
      const nameNorm = norm(c.name);
      const keyNorm = norm(c.key);
      if (nameNorm.includes(q) || keyNorm.includes(q)) {
        if (!seen.has(c.key)) {
          seen.add(c.key);
          out.push(c);
        }
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [collectorSearch]);

  // Log autoscroll
  const logBoxRef = useRef(null);
  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [log]);

  function logLine(message) {
    const ts = new Date().toLocaleTimeString();
    flushSync(() => setLog((prev) => [...prev, `[${ts}] ${String(message || "")}`]));
  }

  function normalizeCollectorKey(value) {
    if (!value) return "";
    const raw = String(value).trim().toLowerCase().replace("(tx)", "").trim();
    for (const { key, name, aliases } of COLLECTORS) {
      const lowName = String(name).toLowerCase();
      if (
        raw === key ||
        raw.includes(key) ||
        raw.includes(`${key} county`) || // allow "Dallas County"
        raw.includes(lowName) ||
        (aliases && aliases.some((a) => raw === a || raw.includes(a)))
      ) {
        return key;
      }
    }
    return raw;
  }

  function addSingle() {
    const key = normalizeCollectorKey(collectorInput);
    let acctRaw = (accountInput || "").trim();

    if (!key || !acctRaw) {
      setError("Enter a CAD / county and account.");
      return;
    }

    // Capture year if provided — all CADs benefit from specifying a year
	let yearVal = null;
	const y = String(yearInput || "").trim();
	if (y) {
	  if (!/^\d{4}$/.test(y)) {
		setError("Year must be a 4-digit value (e.g., 2025).");
		return;
	  }
	  const yi = parseInt(y, 10);
	  if (yi < 1900 || yi > 2100) {
		setError("Year must be between 1900 and 2100.");
		return;
	  }
	  yearVal = yi;
	}
    if (!COLLECTORS.some((c) => c.key === key && !c.comingSoon)) {
      setError(`CAD '${collectorInput}' is not enabled yet.`);
      return;
    }

    // CAD-specific normalization.
    // IMPORTANT:
    //  - We NEVER parse as number, so leading zeros are preserved.
    //  - We keep Smith's leading letter for PINs.
    if (key === "dallas") {
	  // Dallas IDs can include letters in some cases. Keep A–Z/0–9, remove separators/spaces.
	  let cleaned = acctRaw.toUpperCase().replace(/[^A-Z0-9]/g, "");
	  if (!cleaned) {
		setError("Dallas NOV id must contain letters/digits.");
		return;
	  }
	  acctRaw = cleaned;
	} else if (key === "smith") {
      // Smith PIN: must start with a LETTER, alphanumeric only.
      let cleaned = acctRaw.toUpperCase();
      cleaned = cleaned.replace(/[^A-Z0-9]/g, ""); // strip spaces, dashes, etc.

      if (!cleaned) {
        setError("Smith NOV PIN must contain letters/digits.");
        return;
      }
      if (!/^[A-Z][A-Z0-9]*$/.test(cleaned)) {
        setError("Smith NOV PIN must start with a letter.");
        return;
      }

      // keep exactly what we normalized; leading letter preserved
      acctRaw = cleaned;
    } else {
      // Generic: digits-only (leading zeros preserved as part of the string).
      let digits = acctRaw.replace(/\D/g, "");
      if (!digits) {
        setError("Account must contain digits.");
        return;
      }
      acctRaw = digits;
    }

    setQueue((prev) => {
      const exists = prev.some((r) => {
        if (r.collector !== key) return false;
        if (r.account !== acctRaw) return false;
        if (key === "hcad" || key === "collin" || key === "bexar" || key === "tarrant") return r.year === yearVal;
        return true;
      });
      if (exists) return prev;
      const next = [
        ...prev,
        {
          id: prev.length + 1,
          collector: key,
          account: acctRaw,
          year: yearVal,
          status: "Pending",
          filename: "",
        },
      ];
      logLine(`Queued ${key} • ${acctRaw}`);
      return next;
    });
    setAccountInput("");
    setYearInput("");
    setError("");
  }

  // Import from Excel / CSV
  async function previewFile() {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      // Use existing collectorInput as the default CAD / county if file doesn't
      // have its own "CAD"/"County" column.
      fd.append("file", file);
      fd.append("default_cad", collectorInput || "");
      const res = await api.post("/novs/preview", fd);

      const rawRows = Array.isArray(res.data?.rows) ? res.data.rows : [];

      // Backend may send either { cad, account, year } or { collector, account, year }.
      // Normalize into { collector, account, year } for our queue.
      const rows = rawRows.map((r) => ({
        collector: normalizeCollectorKey(String(r.cad || r.collector || "")),
        // Keep as string; do NOT cast to number, so leading zeros stay intact.
        account: String(r.account || "").trim(),
        year: r.year ?? null,
      }));

      // Require year for any HCAD/Collin/Tarrant row in uploads
      const missingRequiredYear = rows.some(
	    (r) =>
		  (r.collector === "hcad" || r.collector === "collin" || r.collector === "bexar" || r.collector === "travis" || r.collector === "tarrant") &&
		  (r.year === null || r.year === undefined)
	  );
	  if (missingRequiredYear) {
	    setError(
		  "HCAD/Collin/Bexar/Travis/Tarrant rows require a notice year column (Year / Tax Year / Notice Year)."
	    );
	    return;
	  }

      // Filter out coming-soon CADs from imports
      const skippedSoon = rows.filter((r) => COMING_SOON.has(r.collector));
      const importable = rows.filter((r) => !COMING_SOON.has(r.collector));
      if (skippedSoon.length > 0) {
        const names = [...new Set(skippedSoon.map((r) => r.collector))].join(", ");
        logLine(`Skipped ${skippedSoon.length} row(s) for coming-soon CAD(s): ${names}`);
      }

      setQueue((prev) => {
        const seen = new Set(
          prev.map((r) => `${r.collector}:${r.account}:${r.year || ""}`)
        );

        const add = importable
          .filter((r) => r.collector && r.account)
          .filter((r) => !seen.has(`${r.collector}:${r.account}:${r.year || ""}`))
          .map((r, idx) => ({
            id: prev.length + idx + 1,
            collector: r.collector,
            account: r.account,
            year: r.year,
            status: "Pending",
            filename: "",
          }));

        const next = [...prev, ...add];
        logLine(
          `Parsed ${rawRows.length} row(s). Added ${add.length}. Queue size is now ${next.length}.`
        );
        return next;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Preview failed");
    } finally {
      setBusy(false);
    }
  }

  function removeAt(index) {
    setQueue((prev) =>
      prev.filter((_, i) => i !== index).map((r, i2) => ({ ...r, id: i2 + 1 }))
    );
  }

  function clearQueue() {
    setQueue([]);
    setSavedCount(0);
    logLine("Queue cleared.");
  }

  function clearCompleted() {
    setQueue((prev) =>
      prev
        .filter((r) => !["Saved", "Error", "Skipped"].includes(r.status))
        .map((r, i) => ({ ...r, id: i + 1 }))
    );
    logLine("Removed rows that were finished.");
  }

  function clearSaved() {
    setQueue((prev) =>
      prev.filter((r) => r.status !== "Saved").map((r, i) => ({ ...r, id: i + 1 }))
    );
    setSavedCount(0);
    logLine("Removed rows that successfully saved.");
  }

  async function runAll() {
    if (!queue.length) {
      setError("Queue at least one row first.");
      return;
    }

    setBusy(true);
    setError("");
    setSavedCount(0);
    logLine(`Starting NOV run for ${queue.length} row(s)…`);
    await paint();

    // ── Check if any queued items need portal auth ──
    const needsAuth = queue.some((r) => AUTH_REQUIRED_CADS.has(r.collector));
    let authCreds = null;
    if (needsAuth) {
      // Always show the modal so the user can confirm or change credentials.
      // Remembered credentials are pre-filled but not auto-submitted.
      authCreds = await waitForCredentials();
      if (!authCreds) {
        logLine("Credential entry cancelled.");
        setBusy(false);
        return;
      }
    }

    // Collect successful PDF blobs so we can retry zip or fall back to individual downloads
    const collected = []; // { filename, blob }
    const okList = [];
    const failList = [];

    // ── Phase 1: fetch all PDFs ──
    for (let i = 0; i < queue.length; i++) {
      const r = queue[i];

      setQueue((prev) =>
        prev.map((row, j) => (j === i ? { ...row, status: "In Progress" } : row))
      );
      logLine(`[${i + 1}/${queue.length}] ${r.collector} • ${r.account}`);
      await paint();

      try {
        const extraHeaders = {};
        if (AUTH_REQUIRED_CADS.has(r.collector) && authCreds) {
          extraHeaders["X-Portal-User"] = authCreds.username;
          extraHeaders["X-Portal-Pass"] = authCreds.password;
        }

        const resp = await api.get(
          `/novs/pdf/${encodeURIComponent(r.collector)}/${encodeURIComponent(
            r.account
          )}`,
          {
            responseType: "blob",
            timeout: CAD_TIMEOUT_MS(r.collector),
            params: r.year ? { year: r.year } : {},
            headers: extraHeaders,
          }
        );

        const filename = `${r.account}_${r.collector}_NOV.pdf`;
        collected.push({ filename, blob: resp.data });
        okList.push({ collector: r.collector, account: r.account, file: filename });

        setQueue((prev) =>
          prev.map((row, j) =>
            j === i ? { ...row, status: "Saved", filename } : row
          )
        );
        setSavedCount((c) => c + 1);
        logLine(`Saved: ${filename}`);
        await paint();
      } catch (err) {
        setQueue((prev) =>
          prev.map((row, j) =>
            j === i ? { ...row, status: "Error", filename: "" } : row
          )
        );
        const reason = err?.response?.data?.detail || err?.message || "fetch failed";
        logLine(`Failed: ${r.collector} • ${r.account} — ${reason}`);
        failList.push({ collector: r.collector, account: r.account, error: reason });
        await paint();
      }
    }

    if (collected.length === 0) {
      logLine("No NOVs saved; nothing to download.");
      setBusy(false);
      return;
    }

    // ── Phase 2: build ZIP (with fallback to individual downloads) ──
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");

    // Helper: download a single blob
    const downloadBlob = (blob, name) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    try {
      logLine(`Building ZIP for ${collected.length} NOV file(s)…`);
      await paint();

      const zip = new JSZip();
      for (const { filename, blob } of collected) {
        zip.file(filename, blob, { compression: "STORE" });
      }

      // Add manifest
      zip.file(
        "manifest.json",
        JSON.stringify(
          { okCount: okList.length, failCount: failList.length, ok: okList, fail: failList },
          null,
          2
        )
      );

      // Add results CSV
      const header = "collector,account,status,filename_or_error\n";
      const csvOk = okList.map((x) => `${x.collector},${x.account},Saved,${x.file}`).join("\n");
      const csvFail = failList
        .map((x) => {
          const safe = String(x.error || "").replace(/[\r\n,]+/g, " ").slice(0, 500);
          return `${x.collector},${x.account},Error,${safe}`;
        })
        .join("\n");
      zip.file("results.csv", header + [csvOk, csvFail].filter(Boolean).join("\n") + "\n", {
        compression: "STORE",
      });

      // Add original import file if present
      if (file) {
        zip.file(file.name || "source_upload.xlsx", file, { compression: "STORE" });
      }

      const content = await zip.generateAsync({
        type: "blob",
        streamFiles: true,
        compression: "STORE",
        compressionOptions: { level: 0 },
      });

      downloadBlob(content, `novs_${stamp}.zip`);
      logLine("✅ Downloaded NOV ZIP.");
    } catch (zipErr) {
      // ── Fallback: download each PDF individually ──
      logLine(`⚠ ZIP generation failed (${zipErr?.message || "unknown"}), downloading files individually…`);
      await paint();

      for (let i = 0; i < collected.length; i++) {
        const { filename, blob } = collected[i];
        try {
          downloadBlob(blob, filename);
          // Small delay so browser doesn't block rapid downloads
          if (i < collected.length - 1) await new Promise((r) => setTimeout(r, 300));
        } catch (dlErr) {
          logLine(`Failed to download ${filename}: ${dlErr?.message || "unknown"}`);
        }
      }
      logLine(`✅ Downloaded ${collected.length} NOV file(s) individually.`);
    }

    setBusy(false);
  }

  function setCollectorFromTile(c) {
    if (COMING_SOON.has(c.key)) {
      logLine(`'${c.key}' is coming soon and disabled.`);
      return;
    }
    setCollectorInput(c.key);
    setShowCollectors(false);
    setTimeout(() => {
      const el = document.getElementById("novs-account-input");
      if (el) el.focus();
    }, 0);
    logLine(`Selected CAD: ${c.key}`);
  }

  const isHcad = normalizeCollectorKey(collectorInput) === "hcad";
  const isCollin = normalizeCollectorKey(collectorInput) === "collin";
  const isBexar = normalizeCollectorKey(collectorInput) === "bexar";
  const isTarrant = normalizeCollectorKey(collectorInput) === "tarrant";

  const completedCount = queue.filter((r) =>
    ["Saved", "Error", "Skipped"].includes(r.status)
  ).length;
  const progressPct = Math.min(
    100,
    Math.round((completedCount / Math.max(1, queue.length)) * 100)
  );

  const handleAppsClick = () => (onClose ? onClose() : navigate("/apps"));

  return (
    <NOVSView
      savedCount={savedCount}
      showCollectors={showCollectors}
      COMING_SOON={COMING_SOON}
      SLOW_COLLECTORS={new Set([...LONG_CADS, ...SLOW_COLLECTORS])}
      regionGroups={REGION_GROUPS}
      collectorSearch={collectorSearch}
      collectorSearchResults={SEARCH_RESULTS}
      onCollectorSearchChange={setCollectorSearch}
      collectorInput={collectorInput}
      accountInput={accountInput}
      yearInput={yearInput}
      isHcad={isHcad}
      isCollin={isCollin}
      isBexar={isBexar}
      isTarrant={isTarrant}
      onYearInputChange={setYearInput}
      file={file}
      fileRef={fileRef}
      queue={queue}
      log={log}
      busy={busy}
      error={error}
      progressPct={progressPct}
      onToggleCollectors={() => setShowCollectors((v) => !v)}
      onCollectorTileClick={setCollectorFromTile}
      onClearSearch={() => setCollectorSearch("")}
      onCollectorInputChange={setCollectorInput}
      onAccountInputChange={setAccountInput}
      onAddSingle={addSingle}
      onPreviewFile={previewFile}
      onFileChange={(f) => setFile(f)}
      onClearFile={() => {
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      }}
      onClearSaved={clearSaved}
      onClearCompleted={clearCompleted}
      onClearQueue={clearQueue}
      onStart={runAll}
      onRemoveAt={removeAt}
      onClearLog={() => setLog([])}
      onAppsClick={handleAppsClick}
      logout={logout}
      logBoxRef={logBoxRef}
      showCredModal={showCredModal}
      credUsername={credUsername}
      credPassword={credPassword}
      credRemember={credRemember}
      onCredUsernameChange={setCredUsername}
      onCredPasswordChange={setCredPassword}
      onCredRememberChange={setCredRemember}
      onCredSubmit={handleCredSubmit}
      onCredCancel={handleCredCancel}
    />
  );
}